import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type NbTheme = 'light' | 'dark';

interface NbColors {
  bgPage: string;
  bgCard: string;
  bgSection: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  accentPrimary: string;
  accentFunctional: string;
  borderStrong: string;
  borderSubtle: string;
  success: { bg: string; text: string };
  warning: { bg: string; text: string };
  error: { bg: string; text: string };
  info: { bg: string; text: string };
}

interface ThemeNbContextType {
  theme: NbTheme;
  colors: NbColors;
  toggleTheme: () => void;
}

const STORAGE_KEY = 'tellowup-nb-theme';

const themes: Record<NbTheme, NbColors> = {
  light: {
    bgPage: '#FFF9F0',
    bgCard: '#FAFAFA',
    bgSection: '#F0F0F0',
    textPrimary: '#1C1C1C',
    textSecondary: '#525252',
    textMuted: '#787878',
    accentPrimary: '#FFD600',
    accentFunctional: '#0D9488',
    borderStrong: '3px solid #1C1C1C',
    borderSubtle: '1px solid #E0E0E0',
    success: { bg: '#000000', text: '#FFFFFF' },
    warning: { bg: '#f59e0b', text: '#000000' },
    error: { bg: '#D64545', text: '#FFFFFF' },
    info: { bg: '#000000', text: '#FFFFFF' },
  },
  dark: {
    bgPage: '#0D0D0D',
    bgCard: '#1C1C1C',
    bgSection: '#161616',
    textPrimary: '#E8E8E8',
    textSecondary: '#A0A0A0',
    textMuted: '#888888',
    accentPrimary: '#FFD600',
    accentFunctional: '#0D9488',
    borderStrong: '3px solid #555555',
    borderSubtle: '1px solid #333333',
    success: { bg: '#000000', text: '#FFFFFF' },
    warning: { bg: '#f59e0b', text: '#000000' },
    error: { bg: '#D64545', text: '#FFFFFF' },
    info: { bg: '#000000', text: '#FFFFFF' },
  },
};

const ThemeNbContext = createContext<ThemeNbContextType | undefined>(undefined);

export function ThemeNbProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<NbTheme>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'dark') return 'dark';
    }
    return 'light';
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-nb-theme', theme);
    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  const toggleTheme = () => setTheme((prev) => (prev === 'light' ? 'dark' : 'light'));

  return (
    <ThemeNbContext.Provider value={{ theme, colors: themes[theme], toggleTheme }}>
      {children}
    </ThemeNbContext.Provider>
  );
}

export function useNbTheme() {
  const context = useContext(ThemeNbContext);
  if (!context) {
    throw new Error('useNbTheme must be used within a ThemeNbProvider');
  }
  return context;
}